import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Box,
  Button,
  Chip,
  Fade,
  Paper,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import camelcase from "camelcase";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { getManagerLevel } from "../../api/account/getManagerLevel";
import {
  getProcedure,
  getProcedureRole,
  getProcedureStages,
} from "../../api/procedure";
import { Files } from "../../components/Files";
import { ManagersList } from "../../components/Managers";
import { ProcedureDetailForm } from "../../components/Procedure";
import { StageList } from "../../components/Stages";
import TenderList from "../../components/Tender/TenderList";
import { useTranslationHook } from "../../hooks";
import { setLoading } from "../../store/UI";
import { ManagerLevel } from "../../types/model/account";
import { Procedure, ProcedureRole } from "../../types/model/procedure";
import { Stage } from "../../types/model/stage";
import { StoreInterfaces } from "../../types/store";

const DetailsProcedure = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslationHook();

  const { accountId, projectId, activityId, procedureId } = useParams<{
    accountId: string;
    projectId: string;
    activityId: string;
    procedureId: string;
  }>();

  const session = useSelector((state: StoreInterfaces) => state.session);

  const [procedureData, setProcedureData] = useState<Procedure | null>(null);
  const [stagesData, setStagesData] = useState<Stage[]>([]);
  const [procedureRole, setProcedureRole] = useState<ProcedureRole | null>(null);
  const [managerLevel, setManagerLevel] = useState<ManagerLevel | null>(null);
  const [managerLevelReady, setManagerLevelReady] = useState(false);
  const [loadingLocal, setLoadingLocal] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const isTenderUser =
    !!session?.accessToken &&
    !session.isAdministrator &&
    !session.isManager;

  const readOnly =
    isTenderUser ||
    (!!managerLevel &&
      managerLevel.level !== "account" &&
      managerLevel.level !== "project" &&
      managerLevel.level !== "activity" &&
      managerLevel.level !== "procedure");

  const fetchManagerLevel = async () => {
    try {
      const data = await getManagerLevel(
        Number(accountId),
        Number(projectId),
        Number(activityId),
        Number(procedureId),
      );
      setManagerLevel(data);
    } catch (error) {
      console.error(error);
    } finally {
      setManagerLevelReady(true);
    }
  };

  const handleStageSelection = (stageId: number) => {
    const stage = stagesData.find((s) => s.id === stageId);
    if (!stage) return;
    navigate(
      `/accounts/${accountId}/projects/${projectId}/activities/${activityId}/procedures/${procedureId}/stages/` +
        camelcase(stage.stageType) +
        `/${stageId}`,
    );
  };

  const statusConfig = () => {
    if (!procedureData) return null;
    if (procedureData.canceled)
      return {
        label: t("procedures.canceled"),
        color: "error" as const,
        icon: <CancelIcon />,
      };
    if (procedureData.closed)
      return {
        label: t("procedures.closed"),
        color: "success" as const,
        icon: <CheckCircleIcon />,
      };
    if (procedureData.started)
      return {
        label: t("procedures.started"),
        color: "secondary" as const,
        icon: <TimelapseIcon />,
      };
    return {
      label: t("procedures.to-be-started"),
      color: "default" as const,
      icon: <RadioButtonUncheckedIcon />,
    };
  };

  const status = statusConfig();

  const fetchData = async () => {
    if (!projectId || !activityId || !procedureId) return;

    dispatch(setLoading(true));
    setLoadingLocal(true);

    try {
      const [procedureRes, stagesRes, roleRes] = await Promise.all([
        getProcedure(
          Number(projectId),
          Number(activityId),
          Number(procedureId),
        ),
        getProcedureStages(
          Number(projectId),
          Number(activityId),
          Number(procedureId),
        ), 
        getProcedureRole(
          Number(projectId),
          Number(activityId),
          Number(procedureId),
        ),
      ]);

      setProcedureData(procedureRes);
      setStagesData(stagesRes);
      setProcedureRole(roleRes);

      enqueueSnackbar(t("generic.data-loaded"), { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("generic.error-loading"), { variant: "error" });
    } finally {
      dispatch(setLoading(false));
      setLoadingLocal(false);
    }
  };

  useEffect(() => {
    if (!isTenderUser) {
      fetchManagerLevel();
    } else {
      setManagerLevelReady(true);
    }
    fetchData();
  }, [projectId, activityId, procedureId]);

  if (loadingLocal || !procedureData || !procedureRole) return null;

  const tabs = [
    {
      id: "details",
      label: t("procedures.tabs.detail"),
      component: managerLevelReady && (
        <ProcedureDetailForm
          accountId={Number(accountId)}
          projectId={Number(projectId)}
          activityId={Number(activityId)}
          procedure={procedureData}
          onUpdate={fetchData}
          readOnly={readOnly}
        />
      ),
    },
    {
      id: "stages",
      label: t("procedures.tabs.stages"),
      component: managerLevelReady && (
        <StageList
          projectId={Number(projectId)}
          activityId={Number(activityId)}
          procedureId={procedureData.id}
          procedure={procedureData}
          procedureRole={procedureRole}
          onStageSelected={handleStageSelection}
          onProcedureUpdate={fetchData}
        />
      ),
    },
    {
      id: "managers",
      label: t("procedures.tabs.managers"),
      component: managerLevelReady && (
        <ManagersList
          accountId={Number(accountId)}
          projectId={Number(projectId)}
          activityId={Number(activityId)}
          procedureId={procedureData.id}
          managerLevel={managerLevel}
          isObjectClosedOrCanceled={
            procedureData.closed || procedureData.canceled
          }
          readOnly={readOnly}
        />
      ),
    },
    ...(procedureRole.isTender
      ? []
      : [
          {
            id: "tenders",
            label: t("procedures.tabs.tenders"),
            component: managerLevel && (
              <TenderList
                accountId={Number(accountId)}
                projectId={Number(projectId)}
                activityId={Number(activityId)}
                procedureId={procedureData.id}
                procedure={procedureData}
                readOnly={readOnly}
              />
            ),
          },
          {
            id: "files",
            label: t("generic.files"),
            component: managerLevel && (
              <Files
                accountId={Number(accountId)}
                projectId={Number(projectId)}
                activityId={Number(activityId)}
                procedureId={procedureData.id}
                readOnly={readOnly}
              />
            ),
          },
        ]),
  ];

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Toolbar>
        <Box
          sx={{
            flex: "1 1 100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="h4">
            {t("generic.procedure")} - {procedureData.name}
          </Typography>
          {status && (
            <Chip
              icon={status.icon}
              label={status.label}
              color={status.color}
              sx={{ borderRadius: "20px", ml: 2 }}
            />
          )}
        </Box>
        <Tooltip title={t("generic.back")}>
          <Button
            onClick={() => navigate(-1)}
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            <ArrowBackIcon />
          </Button>
        </Tooltip>
      </Toolbar>

      <Tabs
        value={tabIndex}
        onChange={(_, value) => setTabIndex(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.id} label={tab.label} />
        ))}
      </Tabs>

      <Box sx={{ p: 3 }}>
        {tabs.map((tab, i) => (
          <Fade
            key={tab.id}
            in={tabIndex === i}
            timeout={{ enter: 200, exit: 0 }}
            unmountOnExit
          >
            <Box>{tab.component}</Box>
          </Fade>
        ))}
      </Box>
    </Paper>
  );
};

export default DetailsProcedure;
